import { useTranslation } from "react-i18next";
import Card from "../../components/Card/Card";
import InputShortText from "../../components/InputShortText/InputShortText";
import { isValidText } from "../../shared/validation";
import SelectInput from "../../components/SelectInput/SelectInput";
import CONFIG from "../../config/config";
import { MeetingType, meetingTypeToTranslationKey } from "../../shared/meetingTypeToTranslationKey";
import TextArea from "../../components/TextArea/TextArea";
import SmallCheckBox from "../../components/SmallCheckBox/SmallCheckBox";
import Toggle from "../../components/Toggle/Toggle";
import ExpandableSection from "../../components/ExpandableSection/ExpandableSection";
import Pill from "../../components/Pill/Pill";
import TinyText from "../TinyText/TinyText";
import SectionTitle from "../SectionTitle/SectionTitle";
import { weekDayToTranslationKey } from "../../shared/weekDayToTranslationKey";
import PriceField from "../PriceField/PriceField";
import { CounterInput } from "../CounterInput/CounterInput";
import { useCallback, useEffect, useMemo, useState } from "react";
import { InputV2 } from "../InputV2/InputV2";
import { ButtonV2 } from "../ButtonV2/ButtonV2";
import { Plus, Trash2 } from "lucide-react";
import PaymentsNotice from "../PaymentsNotice/PaymentsNotice";
import WarningNotice from "../WarningNotice/WarningNotice";

const MIN_FIELD_LENGTH = 1;
const MAX_FIELD_BOOKING_NAME = 150;
const MAX_FIELD_BOOKING_DESCRIPTION = 2000;

type Weekday = "Monday" | "Tuesday" | "Wednesday" | "Thursday" | "Friday" | "Saturday" | "Sunday";

type BudgetRange = {
  min: number;
  max: number;
};

export interface BookingFormType {
  id?: number;
  name: string;
  internalType: MeetingType;
  customerInfo: string;
  automaticallyAccept: boolean;
  duration: string;
  requiresFee: boolean;
  enabled: boolean;
  fee?: number;
  feeCurrency?: string;
  useCalendarAvailability: boolean;
  weekdayHours: Record<Weekday, { enabled: boolean; opening: string; closing: string }>;
  customerData: {
    requestEmail: boolean;
    requestPhone: boolean;
    requestName: boolean;
    requestBudget: boolean;
    requestWeddingDate: boolean;
    requestSize: boolean;
    requestNumberOfPeople: boolean;
    allowSpecialComments: boolean;
    requestWhereHeardFrom: boolean;
  };
  budgetRanges: BudgetRange[];
  brideCanCancel?: boolean;
  requiresRefundableFee: boolean;
  refundableFee?: number;
  refundableFeeCurrency?: string;
  feeIsDeposit?: boolean;
  totalPrice?: number;
  totalPriceCurrency?: string;
  limitBookingsPerBride?: boolean;
  maxBookingsPerBride?: number;
  startDate?: string;
  endDate?: string;
  minimumAdvanceBookingDays?: number;
  maximumAdvanceBookingDays?: number;
  useAdditionalRestrictions: boolean;
  useOnlinePayments: boolean;
  exclusive?: boolean;
  useDisabledDates?: boolean;
  disabledDates?: DisabledDateRange[];
}

type DisabledDateRange = {
  startDate: string; // ISO date format: YYYY-MM-DD
  endDate: string;   // ISO date format: YYYY-MM-DD
}

interface BookingFormProps {
  booking: BookingFormType;
  onChange: (updatedBooking: BookingFormType, errors: Record<string, string>) => void;
  isLoading?: boolean;
}

const BookingForm = ({ booking, onChange, isLoading = false }: BookingFormProps) => {
  const { t } = useTranslation();
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [budgetRanges, setBudgetRanges] = useState(booking.budgetRanges || []);

  const weekdays = useMemo<Weekday[]>(() => 
    ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
  []);

  const validateTimeFormat = (time: string) => {
    const timeRegex = /^([01]\d|2[0-3]):([0-5]\d)$/;
    return timeRegex.test(time);
  };

  const validateHours = useCallback((opening: string, closing: string) => {
    if (!validateTimeFormat(opening)) return t("bookings.form.errors.invalid_time_format");
    if (!validateTimeFormat(closing)) return t("bookings.form.errors.invalid_time_format");
    
    const [openingHour, openingMinute] = opening.split(':').map(Number);
    const [closingHour, closingMinute] = closing.split(':').map(Number);
    
    if (closingHour < openingHour || (closingHour === openingHour && closingMinute <= openingMinute)) {
      return t("bookings.form.errors.closing_before_opening");
    }
    
    return null;
  }, [t]);

  const handleChange = useCallback((updatedFields: Partial<BookingFormType>) => {
    const updatedBooking = { ...booking, ...updatedFields };
    onChange(updatedBooking, errors);
  }, [booking, onChange, errors]);

  useEffect(() => {
    if (booking.useOnlinePayments === undefined) {
      handleChange({ 
        useOnlinePayments: true
      });
    }
  }, [booking.useOnlinePayments, handleChange]);

  const handleCustomerDataChange = (updatedFields: Partial<BookingFormType['customerData']>) => {
    const updatedBooking = { ...booking, customerData: { ...booking.customerData, ...updatedFields } };
    onChange(updatedBooking, errors);
  };

  const validateName = (value: string) => {
    if (!value && (value.trim() !== value)) {
      return t("errors.no_leading_trailing_spaces");
    }
    if (!value || value.length < MIN_FIELD_LENGTH) {
      return t("errors.too_short");
    }
    if (!value || value.length > MAX_FIELD_BOOKING_NAME) {
      return t("errors.too_long");
    }
    return null;
  };
  
  const validateCustomerInfo = (value: string) => {
    if (!value && (value.trim() !== value)) {
      return t("errors.no_leading_trailing_spaces");
    }
    if (!value || value.length < MIN_FIELD_LENGTH) {
      return t("errors.too_short");
    }
    if (!value || value.length > MAX_FIELD_BOOKING_DESCRIPTION) {
      return t("errors.too_long");
    }
    return null;
  };

  const handleNameChange = (value: string) => {
    const trimmedValue = value.trim();
    const error = validateName(trimmedValue);
    const newErrors = { ...errors };
    
    if (error) {
      newErrors.name = error;
    } else {
      delete newErrors.name;
    }
  
    setErrors(newErrors);
    const updatedBooking = { ...booking, name: trimmedValue };
    onChange(updatedBooking, newErrors);
  };
  
  const handleCustomerInfoChange = (value: string) => {
    const trimmedValue = value.trim();
    const error = validateCustomerInfo(trimmedValue);
    const newErrors = { ...errors };
    if (error) {
      newErrors.customerInfo = error;
    } else {
      delete newErrors.customerInfo;
    }
    setErrors(newErrors);
    const updatedBooking = { ...booking, customerInfo: trimmedValue };
    onChange(updatedBooking, newErrors);
  };

  const handleWeekdayHoursChange = (day: Weekday, updatedFields: Partial<BookingFormType['weekdayHours'][Weekday]>) => {
    const updatedHours = {
      ...booking.weekdayHours[day],
      ...updatedFields,
    };
    
    const newErrors = { ...errors };
    if (updatedHours.enabled) {
      const error = validateHours(updatedHours.opening, updatedHours.closing);
      if (error) {
        newErrors[`${day}_hours`] = error;
      } else {
        delete newErrors[`${day}_hours`];
      }
    } else {
      delete newErrors[`${day}_hours`];
    }
    
    setErrors(newErrors);
    const updatedBooking = {
      ...booking,
      weekdayHours: {
        ...booking.weekdayHours,
        [day]: updatedHours,
      },
    };
    onChange(updatedBooking, newErrors);
  };

  const handleAddBudgetRange = () => {
    const newRanges = [...budgetRanges, { min: 500, max: 10000, label: '' }];
    setBudgetRanges(newRanges);
    handleChange({ budgetRanges: newRanges });
  };

  const handleBudgetRangeChange = (index: number, field: keyof BudgetRange, value: number | string) => {
    const newRanges = [...budgetRanges];
    if (field === 'min' || field === 'max') {
      newRanges[index][field] = Number(value);
    }
    setBudgetRanges(newRanges);
    handleChange({ budgetRanges: newRanges });
  };

  const handleDeleteBudgetRange = (index: number) => {
    const newRanges = budgetRanges.filter((_, i) => i !== index);
    setBudgetRanges(newRanges);
    handleChange({ budgetRanges: newRanges });
  };

  useEffect(() => {
    setBudgetRanges(booking.budgetRanges || []);
  }, [booking.budgetRanges]);

  useEffect(() => {
    const newErrors: Record<string, string> = {};
    weekdays.forEach((day) => {
      const { enabled, opening, closing } = booking.weekdayHours[day];
      if (enabled) {
        const error = validateHours(opening, closing);
        if (error) {
          newErrors[`${day}_hours`] = error;
        }
      }
    });
    setErrors(newErrors);
  }, [booking.weekdayHours, weekdays, validateHours]);

  const renderBudgetRangeInputs = (range: BudgetRange, index: number) => (
    <div key={index} className="flex space-x-2 items-center">
      <InputV2
        type="number"
        placeholder={t("bookings.form.budget_ranges.min")!}
        value={range.min}
        onChange={(e) => handleBudgetRangeChange(index, 'min', e.target.value)}
      />
      <InputV2
        type="number"
        placeholder={t("bookings.form.budget_ranges.max")!}
        value={range.max}
        onChange={(e) => handleBudgetRangeChange(index, 'max', e.target.value)}
      />
      <ButtonV2 
        onClick={() => handleDeleteBudgetRange(index)} 
        variant="ghost" 
        size="icon"
        className="text-destructive hover:text-destructive/90"
        aria-label={t("bookings.form.budget_ranges.delete") || undefined}
      >
        <Trash2 className="h-4 w-4" />
      </ButtonV2>
    </div>
  );

  const formatDateForInput = (dateString?: string) => {
    if (!dateString) return '';
    return dateString;
  };

  return (
    <div className="space-y-12">
      <div className="space-y-3">
        <div className="flex flex-col space-y-2">
          <InputShortText
            label={t("bookings.form.name")!}
            // validationFn={(value) => !!value && isValidText(value, MIN_FIELD_LENGTH, MAX_FIELD_BOOKING_NAME)}
            onChange={(e) => handleNameChange(e.target.value)}
            errorMessage={errors.name}
            defaultValue={booking.name}
            isDisabled={isLoading}
          />
          {errors.name && (
            <p className="text-red-500 text-sm">{errors.name}</p>
          )}
        </div>
        <div className="flex flex-wrap">
          <SelectInput
            values={CONFIG.MEETINGS.STATUS.map(e => {
              return { value: e, readableValue: t(meetingTypeToTranslationKey(e as MeetingType)) };
            })}
            onChange={(e) => handleChange({ internalType: e.target.value as MeetingType })}
            label={t("bookings.form.type")!}
            defaultValue={booking.internalType || ''}
            isDisabled={isLoading}
          />
        </div>
        <div className="flex flex-col space-y-2">
          <TextArea
            // validationFn={(value) => !!value && isValidText(value, MIN_FIELD_LENGTH, MAX_FIELD_BOOKING_DESCRIPTION)}
            label={t("bookings.form.description")!}
            onChange={(e) => handleCustomerInfoChange(e.target.value)}
            errorMessage={errors.customerInfo}
            defaultValue={booking.customerInfo}
            isDisabled={isLoading}
            maxLength={MAX_FIELD_BOOKING_DESCRIPTION}
          />
          {errors.customerInfo && (
            <p className="text-red-500 text-sm">{errors.customerInfo}</p>
          )}
        </div>
        <CounterInput
          label={t("bookings.form.duration")!}
          value={(!!booking.duration && Number(booking.duration)) || 60}
          onChange={(e) => handleChange({ duration: String(e) })}
        />
      </div>
      <div className="space-y-3">
        <SectionTitle text={t("bookings.form.availability")} />
        <Card>
          <ExpandableSection
            defaultState={!booking.useCalendarAvailability}
            expandedContent={
              <div className="space-y-3">
                {weekdays.map((day, index) => (
                  <div key={day} className="flex flex-col space-y-2 border px-3 py-2 rounded-xl bg-gray-50">
                    <div className="flex items-center space-x-3">
                      <SmallCheckBox
                        defaultValue={booking.weekdayHours[day].enabled}
                        label={`${weekDayToTranslationKey(index)}`}
                        onChange={(e) => handleWeekdayHoursChange(day, { enabled: e.target.checked })}
                      />
                    </div>
                    {booking.weekdayHours[day].enabled && (
                      <div className="flex flex-col space-y-2">
                        <div className="flex space-x-3 items-center">
                          <InputShortText
                            label={t("bookings.form.day.opening_hours")!}
                            validationFn={(value) => !!value && isValidText(value, 2, 30)}
                            onChange={(e) => handleWeekdayHoursChange(day, { opening: e.target.value })}
                            isDisabled={isLoading}
                            defaultValue={booking.weekdayHours[day].opening}
                          />
                          <InputShortText
                            label={t("bookings.form.day.closing_hours")!}
                            validationFn={(value) => !!value && isValidText(value, 2, 30)}
                            onChange={(e) => handleWeekdayHoursChange(day, { closing: e.target.value })}
                            isDisabled={isLoading}
                            defaultValue={booking.weekdayHours[day].closing}
                          />
                        </div>
                        {errors[`${day}_hours`] && (
                          <p className="text-red-500 text-sm">{errors[`${day}_hours`]}</p>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            }
            collapsedContent={
              <Toggle
                label={t("bookings.form.availability.use_calendar")!}
                defaultValue={booking.useCalendarAvailability}
                isDisabled={isLoading}
                onChange={(value: boolean) => handleChange({ useCalendarAvailability: value })}
              />
            }
          />
        </Card>

        <Card>
          <div className="space-y-4">
            <SectionTitle text={t("bookings.form.additional_restrictions") || "Additional Restrictions"} />
            
            <p className="text-sm text-muted-foreground mb-4">
              {t("bookings.form.additional_restrictions.description") || 
                "Use these options for special events like trunk shows, to prevent last-minute bookings, or to limit how far in advance appointments can be made."}.
            </p>
            
            <Toggle
              label={t("bookings.form.additional_restrictions.show") || "Show additional options"}
              defaultValue={booking.useAdditionalRestrictions || false}
              isDisabled={isLoading}
              onChange={(value: boolean) => {
                handleChange({ useAdditionalRestrictions: value });
                if (!value) {
                  handleChange({
                    useAdditionalRestrictions: false,
                    startDate: undefined,
                    endDate: undefined,
                    minimumAdvanceBookingDays: undefined,
                    maximumAdvanceBookingDays: undefined
                  });
                }
              }}
            />
              
            {booking.useAdditionalRestrictions && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                <div className="space-y-2">
                  <label className="text-sm font-medium">
                    {t("bookings.form.advance_notice.minimum") || "Minimum Advance Notice"}
                  </label>
                  <div className="flex items-center space-x-2">
                    <InputV2
                      type="number"
                      min="0"
                      value={booking.minimumAdvanceBookingDays || ""}
                      onChange={(e) => handleChange({ 
                        minimumAdvanceBookingDays: e.target.value ? Number(e.target.value) : undefined 
                      })}
                      disabled={isLoading}
                      className="flex-1"
                    />
                    <span className="text-sm text-muted-foreground">
                      {t("bookings.form.advance_notice.days") || "days"}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {t("bookings.form.advance_notice.minimum_hint") || "Bookings cannot be made less than this many days in advance"}
                  </p>
                </div>
                
                <div className="space-y-2">
                  <label className="text-sm font-medium">
                    {t("bookings.form.advance_notice.maximum") || "Maximum Booking Window"}
                  </label>
                  <div className="flex items-center space-x-2">
                    <InputV2
                      type="number"
                      min="1"
                      value={booking.maximumAdvanceBookingDays || ""}
                      onChange={(e) => handleChange({ 
                        maximumAdvanceBookingDays: e.target.value ? Number(e.target.value) : undefined 
                      })}
                      disabled={isLoading}
                      className="flex-1"
                    />
                    <span className="text-sm text-muted-foreground">
                      {t("bookings.form.advance_notice.days") || "days"}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {t("bookings.form.advance_notice.maximum_hint") || "Bookings cannot be made more than this many days in the future"}
                  </p>
                </div>
              </div>
            )}

            {booking.useAdditionalRestrictions && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">
                      {t("bookings.form.date_range.start_date") || "Available From Date"}
                    </label>
                    <div className="flex items-center space-x-2">
                      <InputV2
                        type="date"
                        value={formatDateForInput(booking.startDate)}
                        onChange={(e) => handleChange({ startDate: e.target.value || undefined })}
                        disabled={isLoading}
                        className="flex-1"
                      />
                      <ButtonV2 
                        onClick={() => handleChange({ startDate: undefined })} 
                        variant="ghost" 
                        size="icon"
                        className={`h-8 w-8 transition-opacity ${booking.startDate ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
                        type="button"
                        aria-label="Clear start date"
                      >
                        <Trash2 className="h-4 w-4" />
                      </ButtonV2>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {t("bookings.form.date_range.start_date_hint") || "Date from which this booking becomes available"}
                    </p>
                  </div>
                  
                  <div className="space-y-2">
                    <label className="text-sm font-medium">
                      {t("bookings.form.date_range.end_date") || "Available Until Date"}
                    </label>
                    <div className="flex items-center space-x-2">
                      <InputV2
                        type="date"
                        value={formatDateForInput(booking.endDate)}
                        onChange={(e) => handleChange({ endDate: e.target.value || undefined })}
                        disabled={isLoading}
                        className="flex-1"
                      />
                      <ButtonV2 
                        onClick={() => handleChange({ endDate: undefined })} 
                        variant="ghost" 
                        size="icon"
                        className={`h-8 w-8 transition-opacity ${booking.endDate ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
                        type="button"
                        aria-label="Clear end date"
                      >
                        <Trash2 className="h-4 w-4" />
                      </ButtonV2>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {t("bookings.form.date_range.end_date_hint") || "Date after which this booking is no longer available"}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </Card>
        <Card>
          <div className="space-y-4">
            <SectionTitle text={t("bookings.form.disabled_dates") || "Disabled Dates"} />
            <p className="text-sm text-muted-foreground mb-4">
              {t("bookings.form.disabled_dates.description") || 
                "Block specific dates or date ranges from being available for this booking plan. Customers won't be able to book on these dates this offering."}
            </p>
            
            <Toggle
              label={t("bookings.form.disabled_dates.show") || "Show disabled dates options"}
              defaultValue={booking.useDisabledDates || false}
              isDisabled={isLoading}
              onChange={(value: boolean) => {
                handleChange({ useDisabledDates: value });
                if (!value) {
                  handleChange({
                    useDisabledDates: false,
                    disabledDates: undefined
                  });
                }
              }}
            />
            
            {booking.useDisabledDates && (
              <>
                <WarningNotice 
                  message={t("bookings.form.disabled_dates.warning") || 
                    "For general calendar blocking, use the Calendar section to block dates. Disabled dates for booking plans are recommended for special events like trunk shows, where you want to disable this specific offering while keeping other offerings available."}
                />
                
                <div className="space-y-3">
              {(booking.disabledDates || []).map((range, index) => (
                <div key={index} className="flex items-center space-x-2 p-3 border rounded-lg bg-gray-50">
                  <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-2">
                    <div>
                      <label className="text-xs text-muted-foreground mb-1 block">
                        {t("bookings.form.disabled_dates.start_date") || "Start Date"}
                      </label>
                      <InputV2
                        type="date"
                        value={range.startDate}
                        onChange={(e) => {
                          const newRanges = [...(booking.disabledDates || [])];
                          newRanges[index] = { ...range, startDate: e.target.value };
                          // Ensure endDate is not before startDate
                          if (newRanges[index].endDate < e.target.value) {
                            newRanges[index].endDate = e.target.value;
                          }
                          handleChange({ disabledDates: newRanges });
                        }}
                        disabled={isLoading}
                        className="w-full"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground mb-1 block">
                        {t("bookings.form.disabled_dates.end_date") || "End Date"}
                      </label>
                      <div className="flex items-center space-x-2">
                        <InputV2
                          type="date"
                          value={range.endDate}
                          min={range.startDate}
                          onChange={(e) => {
                            const newRanges = [...(booking.disabledDates || [])];
                            newRanges[index] = { ...range, endDate: e.target.value };
                            handleChange({ disabledDates: newRanges });
                          }}
                          disabled={isLoading}
                          className="flex-1"
                        />
                        <ButtonV2
                          onClick={() => {
                            const newRanges = (booking.disabledDates || []).filter((_, i) => i !== index);
                            handleChange({ disabledDates: newRanges });
                          }}
                          variant="ghost"
                          size="icon"
                          className="text-destructive hover:text-destructive/90 h-10 w-10"
                          aria-label={t("bookings.form.disabled_dates.remove") || "Remove disabled date range"}
                          type="button"
                        >
                          <Trash2 className="h-4 w-4" />
                        </ButtonV2>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              
              <ButtonV2
                onClick={() => {
                  const today = new Date().toISOString().split('T')[0];
                  const newRanges = [...(booking.disabledDates || []), { startDate: today, endDate: today }];
                  handleChange({ disabledDates: newRanges });
                }}
                variant="outline"
                className="flex items-center space-x-2"
                disabled={isLoading}
                type="button"
              >
                <Plus className="h-4 w-4" />
                <span>{t("bookings.form.disabled_dates.add") || "Add Disabled Date Range"}</span>
              </ButtonV2>
                </div>
              </>
            )}
          </div>
        </Card>
      </div>
      <div className="space-y-3">
        <SectionTitle text={t("bookings.form.confirmation")} />
        <Card>
          <div className="space-y-3">
            <Toggle
              label={t("bookings.form.confirmation.cancellation")!}
              defaultValue={booking.brideCanCancel}
              isDisabled={isLoading}
              onChange={(value: boolean) => handleChange({ brideCanCancel: value })}
            />
            <div className="mt-2 pt-4">
              <Toggle
                label={t("bookings.form.confiration.automatic")!}
                defaultValue={booking.automaticallyAccept}
                isDisabled={isLoading}
                onChange={(value: boolean) => handleChange({ automaticallyAccept: value })}
              />
              {booking.automaticallyAccept &&
                <span><Pill text={t("global.warning")} color="orange" />&nbsp;<TinyText text={t("bookings.form.confiration.automatic.warning")} /></span>
              }
            </div>
          </div>
        </Card>
      </div>

      
      <div className="space-y-3">
        <SectionTitle text={t("bookings.form.fees")} />
        <Card>
          <div className="space-y-4">
            {/* Fee Toggle */}
            <div>
              <Toggle
                label={t("bookings.form.confirmation.fee")!}
                defaultValue={booking.requiresFee}
                isDisabled={isLoading || booking.requiresRefundableFee}
                onChange={(value: boolean) => {
                  if (value) {
                    handleChange({ requiresFee: true, requiresRefundableFee: false });
                  } else {
                    handleChange({ requiresFee: false, fee: undefined, feeCurrency: undefined });
                  }
                }}
              />
              <p className="text-sm text-muted-foreground mt-1 ml-1">
                {t("bookings.form.confirmation.fee.hint")}.
              </p>
            </div>
            
            {/* Refundable Fee Toggle */}
            <div className="mt-2 pt-4">
              <div>
                <Toggle
                  label={t("bookings.form.confirmation.refundable_fee")!}
                  defaultValue={booking.requiresRefundableFee}
                  isDisabled={isLoading || booking.requiresFee}
                  onChange={(value: boolean) => {
                    if (value) {
                      handleChange({ requiresRefundableFee: true, requiresFee: false });
                    } else {
                      handleChange({ requiresRefundableFee: false, refundableFee: undefined, refundableFeeCurrency: undefined });
                    }
                  }}
                />
                <p className="text-sm text-muted-foreground mt-1 ml-1">
                  {t("bookings.form.confirmation.refundable_fee.hint")}.
                </p>
              </div>
            </div>
            
            {(booking.requiresFee || booking.requiresRefundableFee) && (
              <div className="mt-6 pt-4 border-t border-gray-200">
                <div className="space-y-3">
                  <div>
                    <p className="text-sm font-medium text-gray-900 mb-3">
                      {t("bookings.form.payment_configuration.collection_method") || "How will you collect payments?"}
                    </p>
                    
                    <div className="space-y-3">
                      <div className="flex items-center space-x-3">
                        <input
                          type="radio"
                          id="payment-online"
                          name="useOnlinePayments"
                          value="true"
                          checked={booking.useOnlinePayments === true}
                          onChange={() => handleChange({ useOnlinePayments: true })}
                          disabled={isLoading}
                          className="h-4 w-4 text-primary border-gray-300 focus:ring-primary"
                        />
                        <label htmlFor="payment-online" className="text-sm font-medium">
                          {t("bookings.form.payment_configuration.online") || "Online through eAteliers"}
                        </label>
                      </div>
                      
                      <div className="flex items-center space-x-3">
                        <input
                          type="radio"
                          id="payment-offline"
                          name="useOnlinePayments"
                          value="false"
                          checked={booking.useOnlinePayments === false}
                          onChange={() => handleChange({ useOnlinePayments: false })}
                          disabled={isLoading}
                          className="h-4 w-4 text-primary border-gray-300 focus:ring-primary"
                        />
                        <label htmlFor="payment-offline" className="text-sm font-medium">
                          {t("bookings.form.payment_configuration.offline") || "In-person / Off-platform"}
                        </label>
                      </div>
                    </div>
                  </div>
                  
                  {booking.useOnlinePayments === true && (
                    <PaymentsNotice />
                  )}
                  
                  {booking.useOnlinePayments === false && (
                    <div className="mt-4 p-3 bg-amber-50 rounded-md border border-amber-100">
                      <div className="flex items-start space-x-2">
                        <div className="text-amber-500 mt-0.5">
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="10"></circle>
                            <line x1="12" y1="8" x2="12" y2="12"></line>
                            <line x1="12" y1="16" x2="12.01" y2="16"></line>
                          </svg>
                        </div>
                        <div>
                          <p className="text-sm text-amber-700">
                            {t("bookings.form.payment_configuration.offline_note") || 
                              "You'll collect payments directly from customers. No online payment processing will be configured."}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {booking.requiresFee && (
              <PriceField 
                  label={""}
                  currency={booking.feeCurrency || 'EUR'}
                  price={booking?.fee || 0}
                  currencies={[''].concat(CONFIG.SUPPORTED_CURRENCIES)}
                  onCurrencyChange={e => onChange({ ...booking, feeCurrency: e }, errors)}
                  onPriceChange={value => onChange({ ...booking, fee: value, feeCurrency: booking?.feeCurrency || 'EUR' }, errors)}
                  isDisabled={isLoading}
              />
            )}

            {booking.requiresFee && (
              <div className="space-y-2">
                <Toggle
                  label={t("bookings.form.confirmation.treat_fee_as_deposit") || "Treat fee as a deposit (deduct from total)"}
                  defaultValue={booking.feeIsDeposit || false}
                  isDisabled={isLoading}
                  onChange={(value: boolean) => {
                    if (!value) {
                      handleChange({ feeIsDeposit: false, totalPrice: undefined, totalPriceCurrency: undefined });
                    } else {
                      handleChange({ feeIsDeposit: true, totalPriceCurrency: booking.feeCurrency || 'EUR' });
                    }
                  }}
                />

                {booking.feeIsDeposit && (
                  <div className="mt-2 space-y-2">
                    <PriceField 
                      label={t("bookings.form.deposit.total_price") || "Total service price"}
                      currency={booking.totalPriceCurrency || booking.feeCurrency || 'EUR'}
                      price={booking.totalPrice || 0}
                      currencies={[''].concat(CONFIG.SUPPORTED_CURRENCIES)}
                      onCurrencyChange={e => onChange({ ...booking, totalPriceCurrency: e }, errors)}
                      onPriceChange={value => onChange({ ...booking, totalPrice: value, totalPriceCurrency: booking?.totalPriceCurrency || booking.feeCurrency || 'EUR' }, errors)}
                      isDisabled={isLoading}
                    />
                    {!!booking.totalPrice && typeof booking.fee === 'number' && (
                      <TinyText 
                        text={(t("bookings.form.deposit.remaining_note", { 
                          deposit: `${booking.feeCurrency || 'EUR'} ${booking.fee}`, 
                          remaining: `${booking.totalPriceCurrency || booking.feeCurrency || 'EUR'} ${(Math.max(0, (booking.totalPrice || 0) - (booking.fee || 0))).toFixed(2)}`
                        }) as string) ||
                        `You will collect the remaining ${booking.totalPriceCurrency || booking.feeCurrency || 'EUR'} ${(Math.max(0, (booking.totalPrice || 0) - (booking.fee || 0))).toFixed(2)} in-store or later.`}
                      />
                    )}
                  </div>
                )}
              </div>
            )}
            
            {booking.requiresRefundableFee && (
              <PriceField 
                label={""}
                currency={booking.refundableFeeCurrency || booking.feeCurrency || 'EUR'}
                price={booking?.refundableFee || 0}
                currencies={[''].concat(CONFIG.SUPPORTED_CURRENCIES)}
                onCurrencyChange={e => onChange({ ...booking, refundableFeeCurrency: e }, errors)}
                onPriceChange={value => onChange({ ...booking, refundableFee: value, refundableFeeCurrency: booking?.refundableFeeCurrency || booking.feeCurrency || 'EUR' }, errors)}
                isDisabled={isLoading}
              />
            )}
            


            

          </div>
        </Card>
      </div>
      <div className="space-y-3">
        <SectionTitle text={t("bookings.form.limitations")} />
        <Card>
          <div className="space-y-4">
            <div>
              <Toggle
                label={t("bookings.form.limitations.exclusive")!}
                defaultValue={booking.exclusive || false}
                isDisabled={isLoading}
                onChange={(value: boolean) => handleChange({ exclusive: value })}
              />
              <p className="text-sm text-muted-foreground mt-1 ml-1">
                {t("bookings.form.limitations.exclusive.hint") || "Block other concurrent appointments at the same time"}
              </p>
            </div>
            <div>
              <Toggle
                label={t("bookings.form.limitations.limit_bookings") || "Limit number of bookings per bride"}
                defaultValue={booking.limitBookingsPerBride || false}
                isDisabled={isLoading}
                onChange={(value: boolean) => handleChange({ 
                  limitBookingsPerBride: value,
                  maxBookingsPerBride: value ? (booking.maxBookingsPerBride || 1) : undefined 
                })}
              />
              <p className="text-sm text-muted-foreground mt-1 ml-1">
                {t("bookings.form.limitations.limit_bookings.hint") || "Restrict how many upcoming bookings a bride can have at once"}
              </p>
            </div>
            
            {booking.limitBookingsPerBride && (
              <div className="flex items-center space-x-2">
                <div className="w-32">
                  <InputV2
                    type="number"
                    min={1}
                    placeholder="Max bookings"
                    value={booking.maxBookingsPerBride || 1}
                    onChange={(e) => handleChange({ maxBookingsPerBride: Number(e.target.value) })}
                    disabled={isLoading}
                  />
                </div>
                <span className="text-sm text-muted-foreground">
                  {t("bookings.form.limitations.max_bookings") || "Maximum bookings per bride"}
                </span>
              </div>
            )}
          </div>
        </Card>
      </div>
      <div className="space-y-3">
        <SectionTitle text={t("bookings.form.customer_data")} />
        <Card>
          <>
            <div className="flex justify-between">
              <SmallCheckBox
                defaultValue={booking.customerData.requestSize}
                label={t("bookings.form.customer_data.size")!}
                onChange={(e) => handleCustomerDataChange({ requestSize: e.target.checked })}
              />
              <SmallCheckBox
                defaultValue={booking.customerData.requestWeddingDate}
                label={t("bookings.form.customer_data.wedding_date")!}
                onChange={(e) => handleCustomerDataChange({ requestWeddingDate: e.target.checked })}
              />
            </div>
            <SmallCheckBox
              defaultValue={booking.customerData.requestNumberOfPeople}
              label={t("bookings.form.customer_data.no_of_people")!}
              onChange={(e) => handleCustomerDataChange({ requestNumberOfPeople: e.target.checked })}
            />
            <SmallCheckBox
              defaultValue={booking.customerData.allowSpecialComments}
              label={t("bookings.form.customer_data.special_comments")!}
              onChange={(e) => handleCustomerDataChange({ allowSpecialComments: e.target.checked })}
            />
            <SmallCheckBox
              defaultValue={booking.customerData.requestWhereHeardFrom}
              label={t("bookings.form.customer_data.where_heard")!}
              onChange={(e) => handleCustomerDataChange({ requestWhereHeardFrom: e.target.checked })}
            />
            <div className="flex justify-between">
              <SmallCheckBox
                defaultValue={booking.customerData.requestBudget}
                label={t("bookings.form.customer_data.budget")!}
                onChange={(e) => handleCustomerDataChange({ requestBudget: e.target.checked })}
              />
            </div>
          </>
        </Card>
      </div>
      {booking.customerData.requestBudget && (
        <div className="space-y-3">
          <SectionTitle text={t("bookings.form.budget")} />
            <Card>
              <div className="space-y-4">
                  <>
                  <TinyText text={t("bookings.form.budget_ranges.currency_note", { currency: booking.feeCurrency || 'EUR' })} />
                  {budgetRanges.length > 0 ? (
                    budgetRanges.map((range, index) => renderBudgetRangeInputs(range, index))
                  ) : (
                    <div className="flex justify-center">
                      <ButtonV2 
                        onClick={handleAddBudgetRange}
                        variant="outline"
                        className="flex items-center space-x-2"
                      >
                        <Plus className="h-4 w-4" />
                        <span>{t("bookings.form.budget_ranges.add_first")}</span>
                      </ButtonV2>
                    </div>
                  )}
                  {budgetRanges.length > 0 && (
                    <ButtonV2 onClick={handleAddBudgetRange} className="mt-2">
                      {t("bookings.form.budget_ranges.add")}
                    </ButtonV2>
                  )}
                </>
              </div>
            </Card>
        </div>
      )}
    </div>
  );
};

export default BookingForm;